import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import {
  FaPencilAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCalendar,
  FaRupeeSign,
  FaSpinner,
  FaChartBar,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import {
  format,
  addDays,
  subDays,
  startOfToday,
  isToday,
  isYesterday,
  isTomorrow,
  startOfMonth,
  endOfMonth,
} from 'date-fns';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

const PricingCalendar = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const [startDate, setStartDate] = useState(startOfToday());
  const [selectedDate, setSelectedDate] = useState(startOfToday());
  const [showCalendar, setShowCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  // Fetch price data when dates change
  useEffect(() => {
    const fetchPriceData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate end date (startDate + 6 days for 7-day view)
        const endDate = addDays(startDate, 6);

        const response = await fetch(
          `${AQ_PRICING_BASE_URL}/pricing/api/dynamic-prices-calendar?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPriceData(data);

        // Transform API data to room format
        if (data.room_types && data.room_types.length > 0) {
          const transformedRooms = data.room_types.map((roomType, index) => {
            // Create a map of date to price for quick lookup
            const priceMap = {};
            roomType.prices.forEach((price) => {
              priceMap[price.date] = price;
            });

            return {
              key: `room_${index}`,
              name: roomType.roomtypename,
              priceMap: priceMap,
              // We'll keep the original structure but use priceMap for display
              plans: [
                {
                  type: 'Room Rates',
                  // These will be populated per date from priceMap
                },
              ],
            };
          });

          setRooms(transformedRooms);

          // Initialize collapsed state for all rooms (all expanded by default)
          const initialCollapsed = {};
          transformedRooms.forEach((_, index) => {
            initialCollapsed[`room_${index}`] = false;
          });
          setCollapsed(initialCollapsed);
        }
      } catch (error) {
        console.error('Error fetching price data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPriceData();
  }, [startDate]);

  // Fetch summary data for the current month
  useEffect(() => {
    const fetchSummaryData = async () => {
      setSummaryLoading(true);

      try {
        const currentMonth = format(startDate, 'yyyy-MM');

        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/dynamic-prices-summary`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            month: currentMonth,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSummaryData(data);
      } catch (error) {
        console.error('Error fetching summary data:', error);
        // Don't set main error for summary, just log it
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummaryData();
  }, [startDate]);

  const getNextWeekDates = (startDate) => {
    return Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
  };

  const dates = getNextWeekDates(startDate).map((day) => ({
    date: format(day, 'd'),
    month: format(day, 'MMM'),
    day: format(day, 'EEE'),
    fullDate: day,
    dateString: format(day, 'yyyy-MM-dd'),
    isToday: isToday(day),
    isYesterday: isYesterday(day),
    isTomorrow: isTomorrow(day),
    isSelected: selectedDate && selectedDate.getTime() === day.getTime(),
  }));

  const handleCalendarNavigation = (direction) => {
    const newStartDate = direction === 'next' ? addDays(startDate, 7) : subDays(startDate, 7);
    setStartDate(newStartDate);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleQuickDateChange = (type) => {
    const today = startOfToday();
    let targetDate;

    if (type === 'today') {
      targetDate = today;
    } else if (type === 'yesterday') {
      targetDate = subDays(today, 1);
    } else if (type === 'tomorrow') {
      targetDate = addDays(today, 1);
    }

    setSelectedDate(targetDate);

    // Also adjust the week view to include the selected date
    const startOfWeek = subDays(targetDate, targetDate.getDay());
    setStartDate(startOfWeek);
  };

  const handleCustomDateSelect = (date) => {
    setSelectedDate(date);
    setShowCalendar(false);
    // Set the week view to include the selected date
    const startOfWeek = subDays(date, date.getDay());
    setStartDate(startOfWeek);
  };

  const toggleRoom = (roomKey) => {
    setCollapsed((prev) => ({
      ...prev,
      [roomKey]: !prev[roomKey],
    }));
  };

  const handleUpdateClick = (roomTypeName) => {
    navigate('/dashboard/core-modules/revenue-management/pricing', {
      state: { selectedRoomType: roomTypeName },
    });
  };

  const formatIndianRupees = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPriceForDate = (room, dateString, type) => {
    if (!room.priceMap || !room.priceMap[dateString]) {
      return null;
    }

    const priceData = room.priceMap[dateString];

    if (type === 'base') {
      return priceData.baseprice;
    } else if (type === 'dynamic') {
      return priceData.dynamicprice || priceData.baseprice;
    }

    return null;
  };

  const hasDynamicPricing = (room, dateString) => {
    return (
      room.priceMap && room.priceMap[dateString] && room.priceMap[dateString].has_dynamic_pricing
    );
  };

  const getRoomSummary = (roomName) => {
    if (!summaryData || !summaryData.summary) return null;
    return summaryData.summary.find((item) => item.roomtypename === roomName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-black mx-auto mb-4" />
          <p className="text-gray-600">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto mt-10">
          <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Data</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <header className="flex justify-between items-center mb-3 bg-white p-3 rounded-lg shadow-sm">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="hover:text-gray-600 transition-colors"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="hover:text-gray-600 transition-colors"
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="hover:text-gray-600 transition-colors"
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="hover:text-gray-600 transition-colors"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="text-black font-bold"
          >
            Room Rates
          </button>
        </div>
      </header>

      {/* Quick Date Navigation */}
      <div className="flex justify-end items-center gap-2 mb-4">
        {[
          { label: 'Yesterday', type: 'yesterday' },
          { label: 'Today', type: 'today' },
          { label: 'Tomorrow', type: 'tomorrow' },
        ].map(({ label, type }) => {
          const isActive =
            (type === 'today' && selectedDate && isToday(selectedDate)) ||
            (type === 'yesterday' && selectedDate && isYesterday(selectedDate)) ||
            (type === 'tomorrow' && selectedDate && isTomorrow(selectedDate));

          return (
            <button
              key={type}
              onClick={() => handleQuickDateChange(type)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-black text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          );
        })}

        <div className="relative">
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <FaCalendar className="text-gray-500" size={14} />
            <span>Custom Date</span>
          </button>

          {showCalendar && (
            <div className="absolute right-0 mt-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
              <DatePicker
                selected={selectedDate}
                onChange={handleCustomDateSelect}
                inline
                className="border-0"
              />
            </div>
          )}
        </div>

        {/* Summary Toggle Button */}
        <button
          onClick={() => setShowSummary(!showSummary)}
          className="flex items-center space-x-2 px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
        >
          <FaChartBar size={14} />
          <span>{showSummary ? 'Hide Summary' : 'Show Summary'}</span>
        </button>
      </div>

      {/* Selected Date Info */}
      <div className="mt-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Selected Date</h3>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-md font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded-md">
              {selectedDate && isToday(selectedDate) && 'Today'}
              {selectedDate && isYesterday(selectedDate) && 'Yesterday'}
              {selectedDate && isTomorrow(selectedDate) && 'Tomorrow'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      {showSummary && summaryData && (
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Monthly Summary - {format(startDate, 'MMMM yyyy')}
            </h2>
            {summaryLoading && <FaSpinner className="animate-spin text-gray-500" />}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {summaryData.summary &&
              summaryData.summary.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.roomtypename}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Price:</span>
                      <span className="font-medium flex items-center">
                        <FaRupeeSign size={10} className="mr-1" />
                        {item.baseprice.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dynamic Days:</span>
                      <span className="font-medium">
                        {item.days_with_dynamic} / {item.total_days}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coverage:</span>
                      <span className="font-medium">{item.coverage_percentage}%</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {summaryData.period && (
            <div className="mt-4 text-xs text-gray-500 text-right">
              Period: {summaryData.period.start} to {summaryData.period.end} (
              {summaryData.period.total_days} days)
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
        {/* Calendar Grid */}
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
          {/* Previous Arrow */}
          <button
            onClick={() => handleCalendarNavigation('previous')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors"
          >
            <FaChevronLeft size={16} />
          </button>

          {/* 7-Day Calendar */}
          <div className="grid grid-cols-7 gap-2 flex-grow mx-4">
            {dates.map((day, index) => (
              <button
                key={index}
                onClick={() => handleDateClick(day.fullDate)}
                className={`flex flex-col items-center justify-center py-3 px-2 border rounded-lg transition-all duration-200 ${
                  day.isSelected
                    ? 'bg-black text-white border-black shadow-md'
                    : day.isToday
                      ? 'bg-gray-200 text-gray-700 border-gray-200'
                      : day.isYesterday || day.isTomorrow
                        ? 'bg-gray-100 text-gray-700 border-gray-300'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="text-xs font-semibold uppercase">{day.day}</div>
                <div className="text-lg font-bold mt-1">{day.date}</div>
                <div className="text-xs uppercase opacity-80">{day.month}</div>
                {(day.isToday || day.isYesterday || day.isTomorrow) && (
                  <div className="text-xs font-medium mt-1 opacity-90">
                    {day.isToday && 'Today'}
                    {day.isYesterday && 'Yesterday'}
                    {day.isTomorrow && 'Tomorrow'}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Next Arrow */}
          <button
            onClick={() => handleCalendarNavigation('next')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors"
          >
            <FaChevronRight size={16} />
          </button>
        </div>

        {/* Room Rates */}
        <div className="p-6">
          <div className="space-y-4">
            {rooms.length > 0 ? (
              rooms.map((room, roomIndex) => {
                const roomSummary = getRoomSummary(room.name);

                return (
                  <div
                    key={room.key}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Room Header */}
                    <div
                      className="flex justify-between items-center px-6 py-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => toggleRoom(room.key)}
                    >
                      <div className="flex items-center space-x-4">
                        <h2 className="text-lg font-semibold text-gray-900">{room.name}</h2>
                        {/* {roomSummary && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            {roomSummary.coverage_percentage}% Dynamic
                          </span>
                        )} */}
                      </div>
                      <span className="text-gray-600 text-xl font-bold">
                        {collapsed[room.key] ? '+' : '-'}
                      </span>
                    </div>

                    {/* Room Content */}
                    {!collapsed[room.key] && (
                      <div className="p-6 border-t">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-lg font-semibold text-gray-700">Room Rates</div>
                          <button
                            onClick={() => handleUpdateClick(room.name)}
                            className="flex items-center space-x-2 px-3 py-2 text-white bg-black rounded-md hover:bg-gray-800 transition-colors text-sm"
                          >
                            <FaPencilAlt size={12} />
                            <span>Update</span>
                          </button>
                        </div>

                        {/* Base Rate - Single Row */}
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold text-gray-600 mb-3">Base Rate</h3>
                          <div className="grid grid-cols-7 gap-2 text-center text-sm">
                            {dates.map((day, i) => {
                              const basePrice = getPriceForDate(room, day.dateString, 'base');
                              return (
                                <div
                                  key={i}
                                  className={`flex flex-col items-center justify-center p-3 border rounded-md transition-all ${
                                    day.isSelected
                                      ? 'bg-gray-800 text-white border-gray-700'
                                      : 'bg-gray-50 text-gray-700 border-gray-200'
                                  }`}
                                >
                                  {basePrice ? (
                                    <div className="flex items-center space-x-1 font-semibold">
                                      <FaRupeeSign size={12} />
                                      <span>{Math.round(basePrice).toLocaleString('en-IN')}</span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Final Price - Single Row */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-600 mb-3">Final Price</h3>
                          <div className="grid grid-cols-7 gap-2 text-center text-sm">
                            {dates.map((day, i) => {
                              const finalPrice = getPriceForDate(room, day.dateString, 'dynamic');
                              const hasDynamic = hasDynamicPricing(room, day.dateString);

                              return (
                                <div
                                  key={i}
                                  className={`flex flex-col items-center justify-center p-3 border rounded-md transition-all ${
                                    day.isSelected
                                      ? 'bg-black text-white border-black'
                                      : hasDynamic
                                        ? 'bg-green-50 text-green-700 border-green-200'
                                        : 'bg-blue-50 text-blue-700 border-blue-200'
                                  }`}
                                >
                                  {finalPrice ? (
                                    <>
                                      <div className="flex items-center space-x-1 font-semibold">
                                        <FaRupeeSign size={12} />
                                        <span>
                                          {Math.round(finalPrice).toLocaleString('en-IN')}
                                        </span>
                                      </div>
                                      {hasDynamic && (
                                        <span className="text-xs mt-1 opacity-75">
                                          Dynamic Price
                                        </span>
                                      )}
                                      {!hasDynamic && (
                                        <span className="text-xs mt-1 opacity-75">Base Price</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">No room types available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalendar;
